import { useEffect, useState } from 'react';
import { onAuthStateChanged, User } from 'firebase/auth';
import { doc, onSnapshot } from 'firebase/firestore';
import { auth, db } from '../lib/firebase';
import { getDefaultUserRole } from '../utils/userRoles';
import type { TaskItem } from '../types';

function parseTasks(value: unknown): TaskItem[] | undefined {
  if (!Array.isArray(value)) return undefined;
  const tasks: TaskItem[] = [];
  for (const entry of value) {
    if (
      entry &&
      typeof entry === 'object' &&
      typeof (entry as TaskItem).id === 'string' &&
      typeof (entry as TaskItem).title === 'string' &&
      typeof (entry as TaskItem).done === 'boolean'
    ) {
      const raw = entry as Partial<TaskItem> & Pick<TaskItem, 'id' | 'title' | 'done'>;
      tasks.push({
        id: raw.id,
        title: raw.title,
        done: raw.done,
        createdAt: typeof raw.createdAt === 'string' ? raw.createdAt : '',
        completedAt: typeof raw.completedAt === 'string' ? raw.completedAt : null,
      });
    }
  }
  return tasks;
}

interface GoogleMailIntegration {
  accessToken?: string;
  refreshToken?: string;
  email?: string;
  expiryDate?: number;
  pushToken?: string;
}

interface BudgetData {
  yearlyBudget?: number;
  availableBudget?: number;
}

export interface UserProfile {
  user: User | null;
  email: string | null;
  language: string;
  loading: boolean;
  pushToken?: string;
  googleMailIntegration?: GoogleMailIntegration;
  darkMode: boolean;
  firstName?: string;
  lastName?: string;
  avatar?: string;
  iban?: string;
  insuranceNumber?: string;
  role?: string;
  status?: 'invited' | 'active' | 'blocked' | 'archive' | 'deactivated';
  company?: string;
  deactivationTime?: number;
  deactivationReason?: string;
  deactivatedBy?: string;
  budgetId?: string;
  budget?: {
    [year: string]: BudgetData;
  };
  /** Persisted per-view order of summary stat cards, keyed by viewId. */
  summaryCardOrder?: { [viewId: string]: string[] };
  /** Personal task-tracker list; array order is the user-chosen sort order. */
  tasks?: TaskItem[];
  hasSubscription: boolean;
  subscriptionStatus?: string;
  subscriptionPlan?: 'monthly' | 'yearly' | string;
  subscriptionEnd?: number;
  subscriptionCancelAtPeriodEnd?: boolean;
  stripeCustomerId?: string;
}

function toMillis(value: unknown): number | undefined {
  if (value == null) return undefined;
  if (typeof value === 'number') return value;
  if (typeof value === 'string') {
    const parsed = Date.parse(value);
    return Number.isNaN(parsed) ? undefined : parsed;
  }
  if (typeof value === 'object' && 'toMillis' in value && typeof (value as { toMillis: unknown }).toMillis === 'function') {
    return (value as { toMillis: () => number }).toMillis();
  }
  if (typeof value === 'object' && 'seconds' in value && typeof (value as { seconds: unknown }).seconds === 'number') {
    return (value as { seconds: number }).seconds * 1000;
  }
  return undefined;
}

export const useUserProfile = (): UserProfile => {
  const [user, setUser] = useState<User | null>(null);
  const [email, setEmail] = useState<string | null>(null);
  const [language, setLanguage] = useState<string>('en');
  const [pushToken, setPushToken] = useState<string | undefined>();
  const [googleMailIntegration, setGoogleMailIntegration] = useState<GoogleMailIntegration>();
  const [darkMode, setDarkMode] = useState<boolean>(() => {
    const saved = localStorage.getItem('theme');
    if (saved === 'dark') return true;
    if (saved === 'light') return false;
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  });
  const [firstName, setFirstName] = useState<string>('');
  const [lastName, setLastName] = useState<string>('');
  const [avatar, setAvatar] = useState<string>('');
  const [iban, setIban] = useState<string>('');
  const [insuranceNumber, setInsuranceNumber] = useState<string>('');
  const [role, setRole] = useState<string>('');
  const [status, setStatus] = useState<'invited' | 'active' | 'blocked' | 'archive' | 'deactivated'>('active');
  const [deactivationTime, setDeactivationTime] = useState<number | undefined>();
  const [deactivationReason, setDeactivationReason] = useState<string | undefined>();
  const [deactivatedBy, setDeactivatedBy] = useState<string | undefined>();
  const [company, setCompany] = useState<string>('');
  const [budgetId, setBudgetId] = useState<string | undefined>();
  const [budget, setBudget] = useState<{ [year: string]: BudgetData } | undefined>();
  const [summaryCardOrder, setSummaryCardOrder] = useState<{ [viewId: string]: string[] } | undefined>();
  const [tasks, setTasks] = useState<TaskItem[] | undefined>();
  const [hasSubscription, setHasSubscription] = useState<boolean>(false);
  const [subscriptionStatus, setSubscriptionStatus] = useState<string | undefined>();
  const [subscriptionPlan, setSubscriptionPlan] = useState<string | undefined>();
  const [subscriptionEnd, setSubscriptionEnd] = useState<number | undefined>();
  const [subscriptionCancelAtPeriodEnd, setSubscriptionCancelAtPeriodEnd] = useState<boolean | undefined>();
  const [stripeCustomerId, setStripeCustomerId] = useState<string | undefined>();
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    let unsubscribeDoc: (() => void) | undefined;

    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (!firebaseUser) {
        setUser(null);
        setEmail(null);
        setLanguage('en');
        setPushToken(undefined);
        setGoogleMailIntegration(undefined);
        setFirstName('');
        setLastName('');
        setAvatar('');
        setIban('');
        setInsuranceNumber('');
        setRole('');
        setStatus('active');
        setDeactivationTime(undefined);
        setDeactivationReason(undefined);
        setDeactivatedBy(undefined);
        setCompany('');
        setBudgetId(undefined);
        setBudget(undefined);
        setSummaryCardOrder(undefined);
        setTasks(undefined);
        setHasSubscription(false);
        setSubscriptionStatus(undefined);
        setSubscriptionPlan(undefined);
        setSubscriptionEnd(undefined);
        setSubscriptionCancelAtPeriodEnd(undefined);
        setStripeCustomerId(undefined);
        setLoading(false);
        return;
      }

      setUser(firebaseUser);
      setEmail(firebaseUser.email ?? null);

      try {
        const userRef = doc(db, 'users', firebaseUser.uid);
        unsubscribeDoc = onSnapshot(
          userRef,
          (snap) => {
            if (snap.exists()) {
              const data = snap.data();

              setLanguage(data.language || 'en');
              setPushToken(data.pushToken);
              setGoogleMailIntegration(data.mailIntegration?.google);

              if (typeof data.darkMode === 'boolean') {
                setDarkMode(data.darkMode);
                localStorage.setItem('theme', data.darkMode ? 'dark' : 'light');
              }

              setBudgetId(data.budgetId);
              setBudget(data.budget);
              setSummaryCardOrder(
                data.summaryCardOrder && typeof data.summaryCardOrder === 'object'
                  ? (data.summaryCardOrder as { [viewId: string]: string[] })
                  : undefined,
              );
              setTasks(parseTasks(data.tasks));
              const firstNameValue =
                data.firstName || data.first_name || data.givenName || data.given_name || '';
              const lastNameValue =
                data.lastName || data.last_name || data.familyName || data.family_name || '';

              setFirstName(firstNameValue);
              setLastName(lastNameValue);
              setAvatar(data.avatar || '');
              setIban(data.iban || data.IBAN || '');
              setInsuranceNumber(data.insuranceNumber || '');
              setRole(data.role || getDefaultUserRole());
              setStatus(data.status || 'active');
              setDeactivationTime(data.deactivationTime);
              setDeactivationReason(data.deactivationReason);
              setDeactivatedBy(data.deactivatedBy);
              setCompany(data.company || '');
              setHasSubscription(data.hasSubscription === true);
              setSubscriptionStatus(data.subscriptionStatus);
              setSubscriptionPlan(data.subscriptionPlan);
              setSubscriptionEnd(toMillis(data.subscriptionEnd));
              setSubscriptionCancelAtPeriodEnd(
                typeof data.subscriptionCancelAtPeriodEnd === 'boolean'
                  ? data.subscriptionCancelAtPeriodEnd
                  : undefined
              );
              setStripeCustomerId(data.stripeCustomerId);
            }
          },
          (error) => {
            console.error('Failed to listen to user document:', error);
          }
        );
      } catch (error) {
        console.error('Failed to fetch user profile:', error);
      }

      setLoading(false);
    });

    return () => {
      unsubscribe();
      if (unsubscribeDoc) {
        unsubscribeDoc();
      }
    };
  }, []);

  return {
    user,
    email,
    language,
    loading,
    pushToken,
    googleMailIntegration,
    darkMode,
    firstName,
    lastName,
    avatar,
    iban,
    insuranceNumber,
    role,
    status,
    company,
    budgetId,
    budget,
    summaryCardOrder,
    tasks,
    deactivationTime,
    deactivationReason,
    deactivatedBy,
    hasSubscription,
    subscriptionStatus,
    subscriptionPlan,
    subscriptionEnd,
    subscriptionCancelAtPeriodEnd,
    stripeCustomerId,
  };
};
